import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, MessageCircle, EyeOff, Eye, Trash2, Send, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Dashboard — Comments Moderation
 * Lists every comment across the org's posts. Author can hide, unhide,
 * delete, or reply inline.
 */
export default function DashboardComments() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | visible | hidden | top-level
  const [replyDraft, setReplyDraft] = useState({}); // {commentId: text}
  const [replyingTo, setReplyingTo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/content/comments/mine`, { headers: getAuthHeader() });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error("Could not load comments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => {
    if (filter === "visible") return !c.hidden;
    if (filter === "hidden") return c.hidden;
    if (filter === "top-level") return !c.parent_id;
    return true;
  });

  const onHide = async (id, hidden) => {
    try {
      await axios.patch(`${API}/content/comments/${id}/${hidden ? "unhide" : "hide"}`, {}, { headers: getAuthHeader() });
      setItems((cs) => cs.map((c) => c.id === id ? { ...c, hidden: !hidden } : c));
      toast.success(hidden ? "Comment restored" : "Comment hidden");
    } catch (_) { toast.error("Action failed"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this comment permanently?")) return;
    try {
      await axios.delete(`${API}/content/comments/${id}`, { headers: getAuthHeader() });
      setItems((cs) => cs.filter((c) => c.id !== id && c.parent_id !== id));
      toast.success("Deleted");
    } catch (_) { toast.error("Delete failed"); }
  };

  const onReply = async (parent) => {
    const body = (replyDraft[parent.id] || "").trim();
    if (!body) return;
    try {
      const r = await axios.post(
        `${API}/content/${parent.content_id}/comments/${parent.id}/reply`,
        { body },
        { headers: getAuthHeader() }
      );
      // Attach reply metadata + post_title for display
      setItems((cs) => [{
        ...r.data,
        content_id: parent.content_id,
        post_title: parent.post_title,
        parent_id: parent.id,
      }, ...cs]);
      setReplyDraft((p) => ({ ...p, [parent.id]: "" }));
      setReplyingTo(null);
      toast.success("Reply posted");
    } catch (err) { toast.error(err.response?.data?.detail || "Reply failed"); }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-comments">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-[#00E5FF]" /> Comments
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">Moderate and reply to comments on your published posts.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {[
            { k: "all", label: `All (${items.length})` },
            { k: "visible", label: `Visible (${items.filter(c => !c.hidden).length})` },
            { k: "hidden", label: `Hidden (${items.filter(c => c.hidden).length})` },
            { k: "top-level", label: "Top-level only" },
          ].map(opt => (
            <button
              key={opt.k}
              onClick={() => setFilter(opt.k)}
              className={`px-3 py-1.5 rounded-full border transition ${filter === opt.k ? "bg-[#00E5FF]/15 border-[#00E5FF]/40 text-[#00E5FF]" : "bg-white/5 border-white/10 text-[#94A3B8] hover:bg-white/10"}`}
              data-testid={`filter-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#94A3B8] flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-[#94A3B8] bg-white/5 rounded-xl border border-white/10">
          <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No comments yet. Once visitors comment on your published posts, they'll show up here.
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((c) => (
            <li key={c.id} className={`rounded-xl p-4 border ${c.hidden ? "bg-white/[0.02] border-white/5 opacity-60" : "bg-white/5 border-white/10"}`} data-testid={`comment-row-${c.id}`}>
              <div className="flex items-start gap-3">
                <img src={c.avatar_url} alt={c.name} className="w-9 h-9 rounded-full bg-white/10 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-white font-semibold text-sm">{c.name}</span>
                    {c.is_author_reply && <Badge className="bg-[#10B981]/20 text-[#10B981] text-[10px] px-1.5 py-0">Author</Badge>}
                    {c.parent_id && <Badge className="bg-white/5 text-[#94A3B8] text-[10px] px-1.5 py-0">Reply</Badge>}
                    {c.hidden && <Badge className="bg-[#EF4444]/20 text-[#EF4444] text-[10px] px-1.5 py-0">Hidden</Badge>}
                    <span className="text-[11px] text-[#64748B]">{new Date(c.created_at).toLocaleString()}</span>
                    {!c.is_author_reply && c.email && <span className="text-[11px] text-[#64748B] truncate">• {c.email}</span>}
                  </div>
                  <p className="text-[#E2E8F0] text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="text-[#64748B]">on:</span>
                    <span className="text-[#00E5FF] inline-flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {c.post_title}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    {!c.is_author_reply && !c.parent_id && (
                      <button onClick={() => setReplyingTo(replyingTo === c.id ? null : c.id)} className="text-xs px-2.5 py-1 rounded-md bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25" data-testid={`reply-${c.id}`}>
                        {replyingTo === c.id ? "Cancel" : "Reply"}
                      </button>
                    )}
                    <button onClick={() => onHide(c.id, c.hidden)} className="text-xs px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[#94A3B8] inline-flex items-center gap-1" data-testid={`toggle-hide-${c.id}`}>
                      {c.hidden ? <><Eye className="w-3 h-3" /> Unhide</> : <><EyeOff className="w-3 h-3" /> Hide</>}
                    </button>
                    <button onClick={() => onDelete(c.id)} className="text-xs px-2.5 py-1 rounded-md bg-[#EF4444]/10 hover:bg-[#EF4444]/20 text-[#EF4444] inline-flex items-center gap-1" data-testid={`delete-${c.id}`}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>

                  {replyingTo === c.id && (
                    <div className="mt-3 flex gap-2">
                      <Input
                        autoFocus
                        placeholder="Write a public reply…"
                        value={replyDraft[c.id] || ""}
                        onChange={(e) => setReplyDraft((p) => ({ ...p, [c.id]: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white text-sm"
                        data-testid={`reply-input-${c.id}`}
                      />
                      <Button onClick={() => onReply(c)} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid={`reply-send-${c.id}`}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
